import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { Bell, Plus, Edit, Trash2, RefreshCw, AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react';

const AdminAnnouncementsPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        title: '', content: '', type: 'info', target: 'all', isActive: true
    });

    useEffect(() => { fetchAnnouncements(); }, []);

    const fetchAnnouncements = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/announcements');
            setAnnouncements(res.data.announcements || []);
        } catch (error) {
            toast.error('Failed to load announcements');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/admin/announcements/${editingId}`, form);
                toast.success('Announcement updated');
            } else {
                await api.post('/admin/announcements', form);
                toast.success('Announcement created');
            }
            setShowModal(false);
            setEditingId(null);
            setForm({ title: '', content: '', type: 'info', target: 'all', isActive: true });
            fetchAnnouncements();
        } catch (error) {
            toast.error('Failed to save announcement');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this announcement?')) return;
        try {
            await api.delete(`/admin/announcements/${id}`);
            toast.success('Announcement deleted');
            fetchAnnouncements();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const openEdit = (a) => {
        setEditingId(a.id);
        setForm({ title: a.title, content: a.content, type: a.type, target: a.target, isActive: a.isActive });
        setShowModal(true);
    };

    const getTypeIcon = (type) => {
        const icons = { info: <Info size={16} />, warning: <AlertTriangle size={16} />, critical: <AlertCircle size={16} />, success: <CheckCircle size={16} /> };
        return icons[type] || icons.info;
    };

    const getTypeStyle = (type) => {
        const styles = { info: 'bg-blue-500/20 text-blue-400', warning: 'bg-yellow-500/20 text-yellow-400', critical: 'bg-red-500/20 text-red-400', success: 'bg-green-500/20 text-green-400' };
        return styles[type] || styles.info;
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Bell className="text-yellow-400" /> Announcements</h1>
                        <p className="text-gray-400 text-sm">Manage site-wide notifications</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={fetchAnnouncements} className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"><RefreshCw size={18} /></button>
                        <button onClick={() => { setShowModal(true); setEditingId(null); setForm({ title: '', content: '', type: 'info', target: 'all', isActive: true }); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"><Plus size={18} /> New</button>
                    </div>
                </div>
            </div>

            <div className="p-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading...</div>
                    ) : announcements.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <Bell size={48} className="mx-auto mb-4 text-yellow-400/50" />
                            <p>No announcements yet</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-700">
                            {announcements.map((a) => (
                                <div key={a.id} className="p-4 hover:bg-slate-700/30 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2 rounded-lg ${getTypeStyle(a.type)}`}>{getTypeIcon(a.type)}</div>
                                        <div>
                                            <h3 className="text-white font-medium">{a.title}</h3>
                                            <p className="text-gray-400 text-sm line-clamp-1">{a.content}</p>
                                            <div className="flex gap-2 mt-1">
                                                <span className={`px-2 py-0.5 rounded text-xs ${a.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                    {a.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                                <span className="px-2 py-0.5 rounded text-xs bg-slate-600 text-gray-300">{a.target}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => openEdit(a)} className="p-2 text-blue-400 hover:bg-blue-500/20 rounded"><Edit size={18} /></button>
                                        <button onClick={() => handleDelete(a.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded"><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700">
                        <h2 className="text-xl font-bold text-white mb-4">{editingId ? 'Edit' : 'New'} Announcement</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input type="text" placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                            <textarea placeholder="Content" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white h-24" required />
                            <div className="flex gap-4">
                                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                                    <option value="info">Info</option>
                                    <option value="warning">Warning</option>
                                    <option value="critical">Critical</option>
                                    <option value="success">Success</option>
                                </select>
                                <select value={form.target} onChange={e => setForm({ ...form, target: e.target.value })} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                                    <option value="all">All Users</option>
                                    <option value="verified">Verified Only</option>
                                    <option value="unverified">Unverified Only</option>
                                </select>
                            </div>
                            <label className="flex items-center gap-2 text-white">
                                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" /> Active
                            </label>
                            <div className="flex gap-2 justify-end">
                                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-slate-700 text-white rounded-lg">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAnnouncementsPage;
